import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  InventoryPurchaseOrdersFlatTeamSettingGroupById,
  type InventoryPurchaseOrdersFlatTeamSettingGroupId,
} from "./groups";

type InventoryPurchaseOrdersSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: InventoryPurchaseOrdersSettingInput,
): InventoryPurchaseOrdersSettingInput {
  return metadata;
}

function inventoryPurchaseOrdersGroup<
  const Group extends InventoryPurchaseOrdersFlatTeamSettingGroupId,
  const T extends Record<string, InventoryPurchaseOrdersSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const InventoryPurchaseOrdersRegistry = {
  ...inventoryPurchaseOrdersGroup("inventory_purchase_orders.purchase_orders", {
    "ims/purchaseOrderDefaultLocationId": setting({
      label: "Default buyer address",
      description:
        "Location pre-selected as the buyer address when creating new purchase orders.",
      synonyms: [
        "default buyer address",
        "purchase order location",
        "buyer location",
        "default purchase order location",
      ],
    }),
    "ims/purchaseOrderDefaultCcEmails": setting({
      label: "Default CC emails",
      description:
        "Emails automatically added as CC recipients when emailing purchase orders to suppliers.",
      synonyms: [
        "default cc emails",
        "purchase order cc",
        "cc recipients",
        "supplier email cc",
      ],
    }),
  }),
  ...inventoryPurchaseOrdersGroup(
    "inventory_purchase_orders.recommendation_urgencies",
    {
      "ims/purchaseOrderRecommendationUrgencies": setting({
        label: "Recommendation urgencies",
        description:
          "Urgency levels shown in the Forecasting purchasing recommendations list.",
        synonyms: [
          "recommendation urgencies",
          "purchasing recommendations",
          "forecasting urgencies",
          "urgency levels",
        ],
      }),
    },
  ),
};

type InventoryPurchaseOrdersFlatTeamSettingKey =
  keyof typeof InventoryPurchaseOrdersRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const InventoryPurchaseOrdersFlatTeamSettingKeys = Object.keys(
  InventoryPurchaseOrdersRegistry,
) as InventoryPurchaseOrdersFlatTeamSettingKey[];

export const InventoryPurchaseOrdersFlatTeamSettingCatalogDefinitions =
  InventoryPurchaseOrdersFlatTeamSettingKeys.map((key) => {
    const definition = InventoryPurchaseOrdersRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          InventoryPurchaseOrdersFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    InventoryPurchaseOrdersFlatTeamSettingKey,
    InventoryPurchaseOrdersFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<InventoryPurchaseOrdersFlatTeamSettingKey>
  >[];

export const InventoryPurchaseOrdersFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    InventoryPurchaseOrdersFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<InventoryPurchaseOrdersFlatTeamSettingKey, SettingMetadata>;
